import { State } from 'src/replicant/State';
import {
  Match2FrenzyMilestone,
  Match2FrenzyProgression,
} from 'src/replicant/data/Match2FrenzyProgression.schema';
import RawMatch2FrenzyProgressionBasicV4x from 'src/replicant/data/Match2FrenzyProgression_basic_v4_1.json';
import RawMatch2FrenzyProgressionPowerV4x from 'src/replicant/data/Match2FrenzyProgression_power_v4_1.json';
import RawMatch2FrenzyProgressionBasicV4y from 'src/replicant/data/Match2FrenzyProgression_basic_v4_2.json';
import RawMatch2FrenzyProgressionPowerV4y from 'src/replicant/data/Match2FrenzyProgression_power_v4_2.json';
import {
  Match2FrenzyEventConfig,
  Match2PowerupType,
  getMatch2FrenzyIntroTimestampKey,
  match2UnlockableLevels,
} from 'src/replicant/match2/match2.ruleset';
import {
  getMatch2TimedRewardRemainingTime,
  getMatch2Timestamp,
  isMatch2Available,
} from '../match2.getters';
import { BlockId } from 'src/game/minigame/match2-timestep/game/defs/block';
import { BoosterId } from 'src/game/minigame/match2-timestep/defs/booster';
import {
  Match2Reward,
  PredefinedRewardId,
} from '../rewards/match2-rewards.ruleset';
import { assertNever } from 'src/replicant/utils';
import { getCurrentSchedule } from '../schedules/schedules.getters';
import { frenzySchedule } from './match2-frenzy.ruleset';
import { getJSTDateTime } from 'src/replicant/utils/time';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';

export function isMatch2FrenzyEnabled(state: State, now: number): boolean {
  return (
    canShowMatch2FrenzyBar(state, now) && !hasCompletedMatch2Frenzy(state, now)
  );
}

export function canShowMatch2FrenzyBar(state: State, now: number): boolean {
  const tutorialShown = state.tutorial.basicGameplayShown;
  if (!tutorialShown) {
    return false;
  }

  return isMatch2Available(state) && isMatch2FrenzyActive(state, now);
}

export function isMatch2FrenzyUnlocked(user: State): boolean {
  return (
    !!user.match2?.level && user.match2.level >= match2UnlockableLevels.frenzy
  );
}

/**
 * Get active match2 frenzy event id at the given time
 */
export function getMatch2ActiveFrenzyId(
  state: State,
  now: number,
): string | null {
  const activeFrenzy = getCurrentSchedule(frenzySchedule, now);

  if (!activeFrenzy) {
    return null;
  }

  // Keep old event ids, so users don't lose their progress.
  // TODO: Remove this after these events are over.
  if (
    activeFrenzy.startTime ===
    new Date('2023-12-15T23:00:00.000+0900').getTime()
  ) {
    return '2312_05_leaf_festival';
  }

  if (
    activeFrenzy.startTime ===
    new Date('2023-12-19T23:00:00.000+0900').getTime()
  ) {
    return '2312_06_firecracker_festival';
  }

  return getJSTDateTime(activeFrenzy.startTime).toISO();
}

/**
 * Detect if there is active match2 frenzy event at the given time
 */
function isMatch2FrenzyActive(state: State, now: number): boolean {
  return getMatch2ActiveFrenzyId(state, now) !== null;
}

/**
 * Get active match2 frenzy event config at the given time
 */
export function getMatch2FrenzyEventConfig(
  state: State,
  now: number,
): Match2FrenzyEventConfig | null {
  const frenzyId = getMatch2ActiveFrenzyId(state, now);

  if (!frenzyId) {
    return null;
  }

  const schedule = getCurrentSchedule(frenzySchedule, now);

  if (!schedule) {
    return null;
  }

  const { theme_id, objectives, progressionSet } = schedule;
  let progression: Match2FrenzyProgression | null = null;

  const isNoUnlimitedHammer = isDynamicTestInBucket(
    state,
    ab.tests.TEST_FRENZY_NO_UNLIMITED_HAMMER,
    'enabled',
  );

  const basicV4 = (
    isNoUnlimitedHammer
      ? RawMatch2FrenzyProgressionBasicV4y
      : RawMatch2FrenzyProgressionBasicV4x
  ) as Match2FrenzyProgression;
  const powerV4 = (
    isNoUnlimitedHammer
      ? RawMatch2FrenzyProgressionPowerV4y
      : RawMatch2FrenzyProgressionPowerV4x
  ) as Match2FrenzyProgression;

  switch (progressionSet) {
    case 'basic':
      progression = basicV4;
      break;

    case 'power':
      progression = powerV4;
      break;
  }

  if (!progression) {
    return null;
  }

  return {
    id: frenzyId,
    start: new Date(schedule.startTime),
    duration: schedule.endTime - schedule.startTime,
    theme: theme_id,
    objectives: objectives as BlockId[],
    progression,
  };
}

/**
 * Return match2 frenzy time left in milliseconds
 */
export function getMatch2FrenzyTimeLeft(state: State, now: number): number {
  const activeFrenzy = getMatch2FrenzyEventConfig(state, now);

  if (!activeFrenzy) {
    return 0;
  }

  return new Date(activeFrenzy.start).getTime() + activeFrenzy.duration - now;
}

/**
 * Has completed match2 frenzy
 */
export function hasCompletedMatch2Frenzy(state: State, now: number): boolean {
  const activeFrenzyConfig = getMatch2FrenzyEventConfig(state, now);

  if (!activeFrenzyConfig) {
    return false;
  }

  return (
    isAtLastMatch2FrenzyMilestone(state, now) &&
    getMatch2FrenzyMilestoneProgress(state, now) >=
      activeFrenzyConfig.progression[activeFrenzyConfig.progression.length - 1]
        .requirement
  );
}

function isAtLastMatch2FrenzyMilestone(state: State, now: number): boolean {
  const activeFrenzyConfig = getMatch2FrenzyEventConfig(state, now);

  if (!activeFrenzyConfig) {
    return false;
  }

  return (
    getMatch2FrenzyMilestoneLevel(state, now) ===
    activeFrenzyConfig.progression.length - 1
  );
}

/**
 * Get current match2 frenzy milestone level
 */
export function getMatch2FrenzyMilestoneLevel(
  state: State,
  now: number,
  manualProgress?: number,
): number {
  const activeFrenzy = getMatch2FrenzyEventConfig(state, now);

  if (!activeFrenzy) {
    return 0;
  }

  const progression = activeFrenzy.progression;
  let progress = manualProgress ?? state.match2.frenzy.progress;
  const index = progression.findIndex((milestone) => {
    progress -= milestone.requirement;
    return progress < 0;
  });

  return index === -1 ? progression.length - 1 : index;
}

/**
 * Get current match2 frenzy milestone
 */
export function getMatch2FrenzyMilestone(
  state: State,
  now: number,
  manualProgress?: number,
): Match2FrenzyMilestone | null {
  const activeFrenzy = getMatch2FrenzyEventConfig(state, now);

  if (!activeFrenzy) {
    return null;
  }

  const level = getMatch2FrenzyMilestoneLevel(state, now, manualProgress);
  return activeFrenzy.progression[level] ?? null;
}

/**
 * Get current match2 frenzy progress within a milestone
 */
export function getMatch2FrenzyMilestoneProgress(
  state: State,
  now: number,
  manualProgress?: number,
): number {
  const activeFrenzy = getMatch2FrenzyEventConfig(state, now);
  if (!activeFrenzy) return 0;

  const level = getMatch2FrenzyMilestoneLevel(state, now, manualProgress);
  const progression = activeFrenzy.progression;
  let progress = manualProgress ?? state.match2.frenzy.progress;

  for (let index = 0; index < level; index++) {
    const milestone = progression[index];
    if (progress - milestone.requirement < 0) break;
    progress -= milestone.requirement;
  }

  if (isAtLastMatch2FrenzyMilestone(state, now) && progress) {
    progress = Math.min(
      activeFrenzy.progression[activeFrenzy.progression.length - 1].requirement,
      progress,
    );
  }

  return progress;
}

/**
 * Get current match2 frenzy progress within a milestone
 */
export function getMatch2FrenzyMilestoneReward(
  state: State,
  now: number,
  manualLevel?: number,
): Match2Reward | null {
  const activeFrenzy = getMatch2FrenzyEventConfig(state, now);

  if (!activeFrenzy) {
    return null;
  }

  const level = manualLevel ?? getMatch2FrenzyMilestoneLevel(state, now);
  const milestone = activeFrenzy.progression[level];
  const reward = milestone.reward;

  switch (reward?.type) {
    case 'bPoints':
      return { type: 'bPoints', amount: reward.amount ?? 1 };
    case 'gems':
      return { type: 'gems', amount: reward.amount ?? 1 };
    case 'energy':
      return reward.duration
        ? { type: 'energy', duration: reward.duration }
        : { type: 'energy', amount: reward.amount ?? 1 };
    case 'boosters': {
      const subType = reward.subType as BoosterId;
      return reward.duration
        ? { type: 'boosters', subType, duration: reward.duration }
        : { type: 'boosters', subType, amount: reward.amount ?? 1 };
    }
    case 'powerups': {
      const subType = reward.subType as Match2PowerupType;
      return reward.duration
        ? { type: 'powerups', subType, duration: reward.duration }
        : { type: 'powerups', subType, amount: reward.amount ?? 1 };
    }
    case 'moves':
      return {
        type: 'moves',
        amount: reward.amount ?? 1,
      };
    case 'multiplier':
      return {
        type: 'multiplier',
        subType: 'frenzy',
        // eslint-disable-next-line no-constant-binary-expression
        amount: Number(reward.subType) ?? 2,
        duration: reward.duration ?? 900000,
      };
    case 'powerups_combo':
    case 'wrapper':
      return {
        type: 'wrapper',
        id: reward.subType as PredefinedRewardId,
        amount: reward.amount ?? 1,
        decoration: { type: 'bundle' },
      };
    case 'chest':
      return {
        type: 'wrapper',
        id: reward.subType as PredefinedRewardId,
        amount: reward.amount ?? 1,
        decoration: { type: 'chest', subType: 'pink' },
      };
    default:
      assertNever(reward.type);
  }
}

export function getMatch2UnclaimedFrenzyMilestoneRewards(
  state: State,
  now: number,
): Match2Reward[] {
  const rewards: Match2Reward[] = [];
  state.match2.frenzy.unclaimedMilestone.forEach((level) => {
    const reward = getMatch2FrenzyMilestoneReward(state, now, level);
    if (reward) rewards.push(reward);
  });
  return rewards;
}

/**
 * Get the timestamp of the last time the user saw the frenzy popup.
 */
export function getMatch2FrenzyIntroLastSeen(state: State): number {
  if (!isMatch2FrenzyUnlocked(state)) return 0;
  const key = getMatch2FrenzyIntroTimestampKey();
  return getMatch2Timestamp(state, key);
}

/**
 * Get the active match2 frenzy multiplier - defaults to 1
 */
export function getMatch2FrenzyMultiplier(state: State, now: number): 1 | 2 {
  const duration = getMatch2TimedRewardRemainingTime(
    state,
    'multiplier-frenzy',
    now,
  );
  return duration > 0 ? 2 : 1;
}
